import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content, MAP_URL } from '../translations/content';
import { motion } from 'framer-motion';
import { fadeInUp, fadeInLeft, staggerContainer } from '../utils/animations';
import DentalAnimation from './DentalAnimation';

const Dashboard = () => {
  const { language } = useLanguage();
  const { hero, about, services, contact } = content[language];

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-sky-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900"
      >
        <div className="absolute inset-0 pointer-events-none">
          <DentalAnimation />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 z-10">
          <motion.div variants={fadeInUp} className="text-center max-w-full">
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 sm:mb-8 px-2">
              {hero.title}
            </h1>
            <p className="mt-4 text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-2">
              {hero.subtitle}
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row justify-center items-center gap-4 px-2">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#contact"
                className="w-full sm:w-auto inline-block px-6 py-3 bg-primary-500 text-white dark:bg-primary-600 dark:hover:bg-primary-500 text-base font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 hover:shadow-lg"
              >
                {hero.cta}
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#services"
                className="w-full sm:w-auto inline-block px-6 py-3 bg-white dark:bg-gray-800 text-primary-500 dark:text-primary-400 text-base font-semibold rounded-lg border-2 border-primary-500 dark:border-primary-400 hover:bg-primary-50 dark:hover:bg-gray-700 transition-all duration-300 hover:shadow-lg"
              >
                {services.title}
              </motion.a>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-24 text-white transform translate-y-1" preserveAspectRatio="none" viewBox="0 0 1440 120">
            <path fill="currentColor" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </motion.div>

      {/* Services Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        id="services"
        className="py-24 bg-white dark:bg-gray-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center mb-20">
            <h2 className="text-base text-primary-500 dark:text-primary-400 font-semibold tracking-wide uppercase">
              {services.title}
            </h2>
            <p className="mt-2 text-4xl font-bold text-gray-900 dark:text-white">
              {services.subtitle}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.list.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-gray-100 dark:border-gray-700 hover:border-primary-100 dark:hover:border-primary-400"
              >
                <div className="h-12 w-12 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 group-hover:text-primary-500 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* About Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        id="about"
        className="py-24 bg-gradient-to-b from-white dark:from-gray-900 to-sky-50 dark:to-gray-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeInLeft}>
              <h2 className="text-base text-primary-500 dark:text-primary-400 font-semibold tracking-wide uppercase mb-2">
                {about.title}
              </h2>
              <p className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
                {about.subtitle}
              </p>
              <div className="space-y-6">
                {about.content.map((paragraph, index) => (
                  <p key={index} className="text-lg text-gray-600 dark:text-gray-300">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-10">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{about.memberships.title}</h3>
                <ul className="space-y-4">
                  {about.memberships.list.map((membership, index) => (
                    <li key={index} className="flex items-center text-gray-600 dark:text-gray-300">
                      <svg className="h-6 w-6 text-primary-500 dark:text-primary-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {membership}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp} className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-sky-100 dark:from-gray-800 to-sky-50 dark:to-gray-900 rounded-2xl transform rotate-3"></div>
              <img
                src="https://tandartspraktijkassenberg.nl/.cm4all/iproc.php/tcbruggehoofdfoto.jpg/downsize_1280_0/tcbruggehoofdfoto.jpg"
                alt="Tandartspraktijk Assenberg Building"
                className="relative rounded-2xl shadow-lg w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Contact Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        id="contact"
        className="py-24 bg-gradient-to-br from-primary-50 dark:from-gray-900 to-sky-50 dark:to-gray-800 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-primary-100 dark:bg-grid-primary-900 bg-[size:20px_20px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div variants={fadeInUp} className="text-center mb-20">
            <h2 className="text-base text-primary-500 dark:text-primary-400 font-semibold tracking-wide uppercase">
              {contact.title}
            </h2>
            <p className="mt-2 text-4xl font-bold text-gray-900 dark:text-white">
              {contact.subtitle}
            </p>
          </motion.div>
          <motion.div variants={fadeInLeft} className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{contact.address.title}</h3>
                <a 
                  href={MAP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block text-gray-600 dark:text-gray-300 hover:text-primary-500 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary-500 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="group-hover:text-primary-500 dark:group-hover:text-primary-400">
                      {contact.address.street}<br />
                      {contact.address.city}<br />
                      {contact.address.country}
                    </span>
                  </div>
                </a>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{contact.phoneTitle}</h3>
                <a 
                  href={`tel:${contact.phoneNumber}`}
                  className="group flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-500 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 text-primary-500 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {contact.phoneNumber}
                </a>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{contact.emailTitle}</h3>
                <a 
                  href={`mailto:${contact.emailAddress}`}
                  className="group flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-500 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 text-primary-500 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {contact.emailAddress}
                </a>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{contact.hours.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{contact.hours.schedule}</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{contact.transportation.title}</h3>
                {contact.transportation.content.map((paragraph, index) => (
                  <p key={index} className="text-gray-600 dark:text-gray-300 mb-2">{paragraph}</p>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Dashboard;
